(function initializeTulipAppointments() {
  const CONFIG_SCRIPT_ID = 'tulip-appointments-config';
  const SELECTOR_CONTAINER_ID = 'timekit-project-selector-container';
  const TITLE_SELECTOR = '.tulip-appointments-title';
  const HEADER_SELECTOR = '.timekit-header';
  const SEARCH_BAR_SELECTOR = '.search-bar-wrapper';
  const SUCCESS_FORM_SELECTOR = '.bookingjs-form.success';
  const NAVIGATION_TITLE_SELECTOR = '.timekit-navigation-title';
  const NAVIGATION_DESCRIPTION_SELECTOR = '.timekit-navigation-description';
  const BOOKED_EMAIL_SELECTOR =
    '#booked-email, .booked-email, [name="booked-email"], [data-field-key="booked-email"]';

  const getConfig = () => {
    const configScript = document.getElementById(CONFIG_SCRIPT_ID);
    if (!configScript) return null;

    try {
      return JSON.parse(configScript.textContent || '{}');
    } catch {
      return null;
    }
  };

  /**
   * @param {any} tps
   */
  const installStepTracking = (tps) => {
    if (typeof tps.getStepsFactory !== 'function') return;

    const stepsFactory = tps.getStepsFactory();
    if (!stepsFactory || stepsFactory._vbStepTrackingInstalled) return;

    const getStepNumber = () => {
      const steps = typeof stepsFactory.getSteps === 'function' ? stepsFactory.getSteps() : [];
      const activeStep =
        typeof stepsFactory.currentStep === 'function' ? stepsFactory.currentStep() : null;
      const activeStepIndex = steps.indexOf(activeStep);

      return activeStepIndex >= 0 ? activeStepIndex + 1 : null;
    };

    const updateStepNumber = () => {
      const stepNumber = getStepNumber();
      if (!stepNumber || stepNumber === stepsFactory._vbLastTrackedStepNumber) return;

      stepsFactory._vbLastTrackedStepNumber = stepNumber;

      const selectorContainer = document.getElementById(SELECTOR_CONTAINER_ID);
      if (selectorContainer) {
        selectorContainer.dataset.currentStepNumber = String(stepNumber);
      }

      const title = document.querySelector(TITLE_SELECTOR);
      if (title) {
        title.classList.toggle('tulip-appointments-title--hidden', stepNumber !== 1);
      }

      const timekitHeaders = document.querySelectorAll(HEADER_SELECTOR);
      timekitHeaders.forEach((header) => {
        if (header instanceof HTMLElement) {
          header.style.display = stepNumber > 1 ? '' : 'none';
        }
      });

      const searchBars = document.querySelectorAll(SEARCH_BAR_SELECTOR);
      searchBars.forEach((bar) => {
        bar.classList.toggle('search-bar-wrapper--visible', stepNumber > 1);
      });
    };

    /**
     * @param {string} methodName
     */
    const wrapStepMethod = (methodName) => {
      if (typeof stepsFactory[methodName] !== 'function') return;

      const originalMethod = stepsFactory[methodName].bind(stepsFactory);
      /**
       * @param {...any} args
       */
      const wrappedMethod = (...args) => {
        const result = originalMethod(...args);
        updateStepNumber();
        return result;
      };
      stepsFactory[methodName] = wrappedMethod;
    };

    wrapStepMethod('nextStep');
    wrapStepMethod('previousStep');
    stepsFactory._vbStepTrackingInstalled = true;
    updateStepNumber();
  };

  /**
   * @param {Element | { value?: string; textContent?: string } | null} element
   */
  const getTextOrValue = (element) => {
    if (!element) return '';
    if ('value' in element && typeof element.value === 'string') return element.value.trim();
    return (element.textContent || '').trim();
  };

  /**
   * @param {Element | null} cardBodyElement
   * @returns {string}
   */
  const extractAddressFromCardBody = (cardBodyElement) => {
    if (!cardBodyElement) return '';

    // Store cards render address as first segment followed by <br> city/phone lines.
    const firstLineFromHtml = ((cardBodyElement.innerHTML || '').split(/<br\s*\/?>/i)[0] || '')
      .replace(/<[^>]+>/g, ' ')
      .trim();

    if (firstLineFromHtml) return firstLineFromHtml;

    const lines = (cardBodyElement.textContent || '')
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean);

    if (lines.length === 0) return '';

    return lines[0] || '';
  };

  /**
   * @param {string} address
   * @returns {string}
   */
  const buildDirectionsUrl = (address) => {
    if (!address) return '';
    return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}`;
  };

  /**
   * @param {string} template
   * @param {string} email
   * @returns {string}
   */
  const formatSuccessDescription = (template, email) => {
    if (!template) return '';
    return template
      .replace(/__EMAIL__/g, email || '')
      .replace(/\{\{\s*email\s*\}\}/gi, email || '');
  };

  /**
   * @param {HTMLElement} selectorContainer
   * @returns {string}
   */
  const getSelectedStoreAddress = (selectorContainer) => {
    const previewCardBodies = selectorContainer.querySelectorAll(
      '.user-selection-preview .card-body',
    );
    if (previewCardBodies.length > 0) {
      const lastPreviewCardBody = previewCardBodies[previewCardBodies.length - 1] || null;
      const previewAddress = extractAddressFromCardBody(lastPreviewCardBody);
      if (previewAddress) return previewAddress;
    }

    const selectedCardBody = selectorContainer.querySelector(
      '.selector-card-container .selected .card-body, .selector-card-container .active .card-body',
    );
    if (selectedCardBody) {
      const selectedAddress = extractAddressFromCardBody(selectedCardBody);
      if (selectedAddress) return selectedAddress;
    }

    return '';
  };

  /**
   * @param {HTMLElement} selectorContainer
   * @param {{
   *   successHeading?: string,
   *   successDescriptionTemplate?: string,
   *   appointmentDetailsTitle?: string,
   *   getDirectionsLabel?: string
   * }} config
   */
  const installSuccessStateEnhancements = (selectorContainer, config) => {
    if (selectorContainer.dataset.successEnhancementsInstalled === 'true') return;

    let bookedEmail = '';
    let selectedStoreAddress = '';
    let successStateApplied = false;

    /**
     * @param {ParentNode} [root]
     */
    const updateBookedEmail = (root = selectorContainer) => {
      const emailField = root.querySelector(BOOKED_EMAIL_SELECTOR);
      const emailValue = getTextOrValue(emailField);
      if (emailValue) {
        bookedEmail = emailValue;
      }
    };

    const updateSelectedStoreAddress = () => {
      const selectedAddress = getSelectedStoreAddress(selectorContainer);
      if (selectedAddress) {
        selectedStoreAddress = selectedAddress;
      }
    };

    const updateNavigationCopy = () => {
      const navigationTitle = selectorContainer.querySelector(NAVIGATION_TITLE_SELECTOR);
      if (
        navigationTitle &&
        config.successHeading &&
        navigationTitle.textContent !== config.successHeading
      ) {
        navigationTitle.textContent = config.successHeading;
      }

      const navigationDescription = selectorContainer.querySelector(
        NAVIGATION_DESCRIPTION_SELECTOR,
      );
      if (navigationDescription && config.successDescriptionTemplate) {
        const successDescription = formatSuccessDescription(
          config.successDescriptionTemplate,
          bookedEmail,
        );
        if (navigationDescription.textContent !== successDescription) {
          navigationDescription.textContent = successDescription;
        }
      }
    };

    /**
     * @param {HTMLElement} successForm
     */
    const addSuccessDetails = (successForm) => {
      if (
        !config.appointmentDetailsTitle ||
        !config.getDirectionsLabel ||
        successForm.querySelector('.tulip-success-details')
      )
        return;

      const detailsContainer = document.createElement('div');
      detailsContainer.className = 'tulip-success-details';

      const detailsTitle = document.createElement('h3');
      detailsTitle.className = 'tulip-success-details__title';
      detailsTitle.textContent = config.appointmentDetailsTitle;
      detailsContainer.appendChild(detailsTitle);

      const directionsButton = document.createElement('button');
      directionsButton.type = 'button';
      directionsButton.className = 'button button-unstyled tulip-success-directions-button';
      directionsButton.textContent = config.getDirectionsLabel;

      const mapsUrl = buildDirectionsUrl(selectedStoreAddress);
      if (mapsUrl) {
        directionsButton.addEventListener('click', () => {
          window.open(mapsUrl, '_blank', 'noopener,noreferrer');
        });
      } else {
        directionsButton.disabled = true;
      }

      detailsContainer.appendChild(directionsButton);
      successForm.appendChild(detailsContainer);
    };

    /**
     * Move booking details into the success details container so layout order is explicit.
     * @param {HTMLElement} successForm
     */
    const reorderSuccessLayout = (successForm) => {
      const detailsContainer = successForm.querySelector('.tulip-success-details');
      if (!(detailsContainer instanceof HTMLElement)) return;

      if (detailsContainer.dataset.reordered === 'true') return;

      const directionsButton = detailsContainer.querySelector('.tulip-success-directions-button');
      if (!(directionsButton instanceof HTMLElement)) return;

      const bookpage = successForm.closest('.bookingjs-bookpage');
      if (!(bookpage instanceof HTMLElement)) return;

      const dateElement = bookpage.querySelector('.bookingjs-bookpage-date');
      if (dateElement) {
        detailsContainer.insertBefore(dateElement, directionsButton);
      }

      const timeElement = bookpage.querySelector('.bookingjs-bookpage-time');
      const resourceElement = bookpage.querySelector('.bookingjs-bookpage-resource');

      if (timeElement || resourceElement) {
        const timeResourceRow = document.createElement('div');
        timeResourceRow.className = 'tulip-success-time-resource';

        if (timeElement) {
          timeResourceRow.appendChild(timeElement);
        }

        if (resourceElement) {
          timeResourceRow.appendChild(resourceElement);
        }

        detailsContainer.insertBefore(timeResourceRow, directionsButton);
      }

      detailsContainer.dataset.reordered = 'true';
    };

    const enhanceSuccessState = () => {
      updateBookedEmail();
      updateSelectedStoreAddress();

      const successForms = selectorContainer.querySelectorAll(SUCCESS_FORM_SELECTOR);
      if (successForms.length === 0) return false;

      updateNavigationCopy();
      successForms.forEach((successForm) => {
        if (successForm instanceof HTMLElement) {
          addSuccessDetails(successForm);
          reorderSuccessLayout(successForm);
        }
      });

      return true;
    };

    /**
     * @param {Event} event
     */
    const onEmailInput = (event) => {
      const target = event.target;
      if (!(target instanceof Element) || !target.matches(BOOKED_EMAIL_SELECTOR)) return;
      const inputValue = getTextOrValue(target);
      if (inputValue) {
        bookedEmail = inputValue;
      }
    };

    /**
     * @param {Event} event
     */
    const onStoreSelection = (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const currentStepNumber = Number(selectorContainer.dataset.currentStepNumber || '0');
      if (currentStepNumber !== 2) return;

      const cardContainer = target.closest('.card-container, .card-container-image');
      if (!cardContainer) return;

      const cardBody = cardContainer.querySelector('.card-body');
      const selectedAddress = extractAddressFromCardBody(cardBody);
      if (selectedAddress) {
        selectedStoreAddress = selectedAddress;
      }
    };

    const observer = new MutationObserver(() => {
      if (successStateApplied) return;

      const didApplySuccessState = enhanceSuccessState();
      if (didApplySuccessState) {
        successStateApplied = true;
        observer.disconnect();
      }
    });

    observer.observe(selectorContainer, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class'],
    });

    selectorContainer.addEventListener('input', onEmailInput, true);
    selectorContainer.addEventListener('change', onEmailInput, true);
    selectorContainer.addEventListener('click', onStoreSelection, true);

    selectorContainer.dataset.successEnhancementsInstalled = 'true';
    successStateApplied = enhanceSuccessState();
    if (successStateApplied) {
      observer.disconnect();
    }
  };

  const initialize = () => {
    const config = getConfig();
    const selectorContainer = document.getElementById(SELECTOR_CONTAINER_ID);
    const tps = /** @type {any} */ (window).timekit_project_selector;

    if (!config || !config.appKey || !selectorContainer || !tps || typeof tps.init !== 'function')
      return;

    tps
      .init({
        embed: true,
        defaultUI: true,
        includePrivateAppointments: true,
        api_base_url: 'https://api.timekit.io',
        app_key: config.appKey,
        shouldConsiderAssociateAvailability: true,
        selectorOptions: {
          global_appointment_type_project: {
            title: config.selectAppointmentTypeTitle,
            description: config.selectAppointmentTypeDescription,
            card_image: '[meta]t_appointment_type_image',
            card_title: '[meta]t_appointment_type_name',
            card_body: '[meta]t_appointment_type_description',
            card_footer: `<button class="button button-unstyled">${config.selectLabel}</button>`,
            filters: {
              t_disabled: 0,
              t_private: 0,
            },
          },
          store_project: {
            title: config.selectStoreTitle,
            description: config.selectStoreDescription,
            card_title: '[meta]t_store_name',
            card_body:
              '{{[meta]t_store_address}}<br>{{[meta]t_store_city}}<br>{{[meta]t_store_phone}}',
            card_footer: `<button class="button button-unstyled">${config.selectLabel}</button>`,
            search_bar: {
              enabled: true,
              placeholder: config.searchForCityPlaceholder,
            },
            strategy: 'store_project',
          },
        },
      })
      .then(() => {
        selectorContainer.classList.add('open');
        installStepTracking(tps);
        installSuccessStateEnhancements(selectorContainer, config);

        const headerRight = document.getElementById('timekit-header-right');
        if (!headerRight) return;

        headerRight.onclick = () => {
          const stylistChat = document.getElementById('stylist-chat');
          if (stylistChat) {
            stylistChat.classList.remove('disable-click');
          }

          const stylistChatText = document.getElementById('stylist-chat-text');
          if (stylistChatText) {
            stylistChatText.classList.remove('disable-click');
          }

          selectorContainer.classList.remove('open');

          const floatingButton = document.getElementById('floating-button');
          if (floatingButton) {
            floatingButton.style.display = 'none';
          }
        };
      })
      .catch(() => {});
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
    return;
  }

  initialize();
})();
